import { useEffect, useMemo, useState } from 'react';
import { Person, PersonOutline } from '@mui/icons-material';
import { capitalize } from '@mui/material';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { ListPage, ListRow } from '/src/components';
import { useFilterStore, useUserStore } from '/src/context';
import { DataModelType, UserRole, UserStatus } from '/src/lib/types';

/**
 * List Users Page
 */
export const ListUsersPage = observer(() => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;

  /** Context **/
  const userStore = useUserStore();
  const filterStore = useFilterStore();

  /** State **/
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);

  /** Computed **/
  const users = userStore.users;

  const companies = filterStore.companyListGet();
  const lenders = filterStore.lenderListGet();

  const listData = useMemo(() => {
    if (users) {
      const list = users
        .map((user) => {
          if (user.roles[0] === UserRole.Company) {
            user.companyDisplay =
              companies?.find((company) => typeof user.company === 'string' && company._id === user.company)?.name ||
              '';
          } else if (user.roles[0] === UserRole.Investor) {
            if (Array.isArray(lenders)) {
              user.companyDisplay =
                lenders?.find((lender) => typeof user.company === 'string' && lender._id === user.company)?.name || '';
            }
          }
          return user;
        })
        .toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      return list;
    } else return [];
  }, [users, sortType, sortReverse, companies, lenders]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  /** Effects **/

  useEffect(() => {
    userStore.query();

    return () => (userStore.users = undefined);
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.User}
      createVerb="Create"
      headerItems={[
        { flex: 1, propName: 'status', label: 'Status' },
        { flex: 1, propName: 'username', label: 'Username' },
        { flex: 1, propName: 'displayName', label: 'Display Name' },
        { flex: 1, propName: 'email', label: 'Email' },
        { flex: 1, propName: 'role', label: 'User Role' },
        { flex: 1, propName: 'company', label: 'Company' },
      ]}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
    >
      {listData?.map((user, i) => (
        <ListRow
          key={user._id}
          padForScrollbar={listData.length > 11}
          items={[
            {
              propName: 'status',
              content: capitalize(user.status),
              justifyContent: 'start',
              icon: user.status === UserStatus.Active ? <Person /> : <PersonOutline />,
            },
            { propName: 'username', content: user.username, url: `/users/${user._id}` },
            { propName: 'displayName', content: user.displayName },
            { propName: 'email', content: user.email },
            { propName: 'role', content: capitalize(user.roles[0]) },
            { propName: 'company', content: user.companyDisplay },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
